import {EmployeeRef, KeyOfficeVisitReport, OfficeVisit, OfficeVisitSheet} from "../../api/Model";
import {associateBy, groupBy, mapValues} from "../../util/MapUtil";
import {AssignedDeskCriteria} from "./AssignedDeskCriteria";
import {AssignedDesks} from "../../api/AssignedDeskStore";
import {numberCmp} from "../../util/SortUtil";
import {dateRange, isoDate, isWeekend, WeekDay} from "../../util/DateUtil";
import {ensureOfficeVisitReportIncludes, filterOfficeVisitReport} from "../../util/visit/VisitReportSheetUtil";
import {filterDateRange, filterValue} from "../../util/FilterUtil";
import {idFn, selfFn} from "../../util/FunctionUtil";
import {EmployeeVisitSheet, EmployeeVisitType,} from "../../api/EmployeeVisitStore";
import {DateRangeOption} from "../filter-fields/DateRangeFilter";
import {Opt} from "../../util/Optional";

export type StatFigure = {
    value: number,
    percent: number,
};

function statsFigure(value: number, total: number): StatFigure {
    return {
        value: Math.round(value * 10) / 10,
        percent: total !== 0 ? Math.round(value / total * 100) : Number.POSITIVE_INFINITY,
    }
}

export interface VisitStats {
    visits: OfficeVisit[];
    overflowDays: number;
    maxVisits: StatFigure;
    avgVisits: StatFigure;
    percentile90: StatFigure;
    median: StatFigure;
}

export type StatRanges = Record<
    keyof Omit<VisitStats, 'visits'>,
    {min: number | undefined, max: number | undefined}
>;

export const emptyStatRanges: StatRanges = {
    overflowDays: {min: undefined, max: undefined},
    maxVisits: {min: undefined, max: undefined},
    avgVisits: {min: undefined, max: undefined},
    percentile90: {min: undefined, max: undefined},
    median: {min: undefined, max: undefined},
};

export function calculateStatRanges(rows: readonly VisitStats[]) {
    function combine(acc: {min: number | undefined, max: number | undefined}, value: number) {
        return Number.isFinite(value) ? {
            min: acc.min === undefined ? value : Math.min(acc.min, value),
            max: acc.max === undefined ? value : Math.max(acc.max, value),
        } : acc;
    }
    return rows.reduce<StatRanges>((acc, rows) => ({
        overflowDays: combine(acc.overflowDays, rows.overflowDays),
        maxVisits: combine(acc.maxVisits, rows.maxVisits.percent),
        avgVisits: combine(acc.avgVisits, rows.avgVisits.percent),
        percentile90: combine(acc.percentile90, rows.percentile90.percent),
        median: combine(acc.median, rows.median.percent),
    }), emptyStatRanges);
}

export interface VisitWithAssignedDesksStats extends VisitStats {
    assignedDeskCriteria: AssignedDeskCriteria;
    employeeStats: {
        employees: {
            employee: EmployeeRef,
            visits: StatFigure,
        }[],
        assignedDeskRecommendationEmployees: Set<EmployeeRef['id']>,
    };
}

function percentile(sorted: number[], percentile: number): number {
    if (sorted.length === 0) {
        return 0;
    } else if (sorted.length === 1) {
        return sorted[0];
    } else {
        const p = Math.min(100, Math.max(0, percentile)) / 100;

        // position in [0, n-1]
        const h = (sorted.length - 1) * p;
        const lower = Math.floor(h);
        const upper = Math.ceil(h);

        // If h is an integer index, no interpolation needed
        if (lower === upper) return sorted[lower];

        const weight = h - lower; // in (0, 1)
        return sorted[lower] * (1 - weight) + sorted[upper] * weight;
    }
}

export function calculateBasicFigures(values: number[]) {
    const max = values.length ? Math.max(...values) : 0;
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = values.length ? sum / values.length : 0;
    const sorted = [...values].sort(numberCmp((it: number) => it));
    const median = percentile(sorted, 50);
    const p90 = percentile(sorted, 90);
    return {max, avg, median, p90};
}


export function calculateVisitStatistics(officeVisits: OfficeVisit[], deskCount: number): VisitStats {
    const visitorCounts = officeVisits
        .filter(visit => visit.type === 'work-day')
        .map(visit => visit.visitorCount);
    const basicFigures = calculateBasicFigures(visitorCounts);

    // Update stats
    return {
        visits: officeVisits,
        overflowDays: visitorCounts.filter(count => count > deskCount).length,
        maxVisits: statsFigure(basicFigures.max, deskCount),
        avgVisits: statsFigure(basicFigures.avg, deskCount),
        percentile90: statsFigure(basicFigures.p90, deskCount),
        median: statsFigure(basicFigures.median, deskCount),
    };
}

function doCalculateVisitWithAssignedDesksStatistics(
    visitSheet: EmployeeVisitSheet,
    assignedDesks: AssignedDesks,
    assignedDeskCriteria: AssignedDeskCriteria,
    deskCount: number,
): VisitWithAssignedDesksStats {
    const allVisitDates = visitSheet.dates.map<Omit<OfficeVisit, 'visitorCount'>>(date => ({
        date,
        type: isWeekend(date) ? 'weekend' : 'work-day',
    }));

    const employeeVisitCounts: Map<EmployeeRef['id'], number> = associateBy(
        visitSheet.rows,
        row => row.value.id,
        row => row.visits.filter(visit => visit === EmployeeVisitType.WorkDayVisit).length,
    );
    const employeeEligibleWorkDayCounts: Map<EmployeeRef['id'], number> = associateBy(
        visitSheet.rows,
        row => row.value.id,
        row => row.visits.filter(
            (visit, dateIndex) =>
                allVisitDates[dateIndex].type === 'work-day' && visit !== EmployeeVisitType.BeforeStart
        ).length,
    );

    const employeeWithAssignedDesksIds: Set<EmployeeRef['id']> = new Set(
        visitSheet.rows
            .filter(row => assignedDeskCriteria.hasAssignedDesk(
                row.value,
                assignedDesks,
                employeeVisitCounts,
                employeeEligibleWorkDayCounts.get(row.value.id) || 0,
            ))
            .map(row => row.value.id)
    );

    const visits: OfficeVisit[] = allVisitDates
        .map<OfficeVisit>((d, dateIndex) => ({
            ...d,
            visitorCount: d.type === 'work-day'
                ? visitSheet.rows.reduce<number>((sum, row) => {
                    const visit = row.visits[dateIndex];
                    return sum + (
                        employeeWithAssignedDesksIds.has(row.value.id)
                            ? visit !== EmployeeVisitType.BeforeStart ? 1 : 0
                            : visit === EmployeeVisitType.WorkDayVisit ? 1 : 0
                    );
                }, 0)
                : visitSheet.rows
                    .reduce<number>(
                        (sum, row) => sum + (row.visits[dateIndex] === EmployeeVisitType.WeekendVisit ? 1 : 0),
                        0,
                    )
        }));

    return {
        ...calculateVisitStatistics(visits, deskCount),
        assignedDeskCriteria,
        employeeStats: {
            employees: visitSheet.rows
                .map(row => ({
                    employee: row.value,
                    visits: statsFigure(
                        employeeVisitCounts.get(row.value.id) || 0,
                        employeeEligibleWorkDayCounts.get(row.value.id) || 0,
                    ),
                }))
                .sort(numberCmp(it => it.visits.value, true)),
            assignedDeskRecommendationEmployees: employeeWithAssignedDesksIds,
        }
    };
}


export function calculateVisitWithAssignedDesksStatistics(
    employeeVisits: EmployeeVisitSheet,
    relevantEmployees: EmployeeRef[],
    assignedDesks: AssignedDesks,
    assignedDeskCriteria: AssignedDeskCriteria,
    dateRange: Opt<DateRangeOption>,
    deskCount: number,
): VisitWithAssignedDesksStats {
    const relevantEmployeeIds = new Set(relevantEmployees.map(idFn));
    return doCalculateVisitWithAssignedDesksStatistics(
        filterOfficeVisitReport(
            ensureOfficeVisitReportIncludes(
                employeeVisits,
                relevantEmployees,
                idFn,
                EmployeeVisitType.Absent,
            ),
            filterDateRange(dateRange),
            filterValue(
                (employee: EmployeeRef) => relevantEmployeeIds.has(employee.id),
                selfFn,
            ),
        ),
        assignedDesks,
        assignedDeskCriteria,
        deskCount,
    );
}

export function calculateKeyVisitCountPerDate(
    visitSheets: Map<KeyOfficeVisitReport["id"], OfficeVisitSheet<string>>
): Map<KeyOfficeVisitReport["id"], OfficeVisit[]> {
    return mapValues(
        visitSheets,
        keyVisitSheet => {
            const date2index = new Map(
                keyVisitSheet.dates.map((date, dateIndex) => [date, dateIndex])
            );

            const [fromDate, toDate] = keyVisitSheet.dates
                .map(date => new Date(date))
                .reduce(([prevFrom, prevTo], date) => {
                    return [
                        prevFrom === undefined ? date : prevFrom <= date ? prevFrom : date,
                        prevTo === undefined ? date : prevTo >= date ? prevTo : date,
                    ];
                }, [undefined, undefined] as [Date | undefined, Date | undefined]);

            const wholeDateRange = fromDate !== undefined && toDate !== undefined
                // 256 is to prevent an explosion in case the dates are too distant from each other
                ? dateRange(fromDate, toDate).slice(0, 256)
                : [];

            return wholeDateRange.map<OfficeVisit>(date => {
                const iso = isoDate(date);
                const dateIndex = date2index.get(iso);
                return {
                    date: iso,
                    type: date.getDay() === WeekDay.Saturday || date.getDay() === WeekDay.Sunday
                        ? 'weekend'
                        : 'work-day',
                    visitorCount: keyVisitSheet.rows
                        .filter(row => dateIndex !== undefined ? row.visits[dateIndex] : false)
                        .length,
                };
            });
        }
    );
}

/**
 * Calculates the cumulative number of rows that visited the office at least a given number of days.
 *
 * @param sheets The `OfficeVisitSheet` containing rows and their respective visit data.
 * @return An array where each element represents the cumulative count of rows that visited
 * the office at least that many days, indexed by the number of days.
 */
export function calculateCumulativeDistribution(sheets: Map<KeyOfficeVisitReport["id"], OfficeVisitSheet<any>>): Map<KeyOfficeVisitReport["id"], number[]> {
    return mapValues(sheets, sheet => {
        const key2visitCount = groupBy(
            sheet.rows,
            row => row.visits.filter(hasVisited => hasVisited).length,
        );

        // dayVisitors[N] — rows that visited office exactly N number of days
        const dayVisitors = Array.from(
            {length: sheet.dates.length + 1},
            (_, numberOfVisits) => key2visitCount.get(numberOfVisits)?.length || 0
        );

        // accDayVisitors[N] — rows that visited office at least N number of days
        const accDayVisitors = new Array<number>(dayVisitors.length).fill(0);
        for (let numberOfVisits = dayVisitors.length - 1; numberOfVisits >= 0; numberOfVisits--) {
            accDayVisitors[numberOfVisits] = dayVisitors[numberOfVisits] +
                (numberOfVisits < dayVisitors.length - 1 ? accDayVisitors[numberOfVisits + 1] : 0);
        }
        return accDayVisitors;
    });
}
